import LeadEvent, {LeadEventTypeEnum} from "./LeadEvent";
import * as moment from "moment";
import {LeadAssigneeEventDataApiDto, LeadEventApiDto} from "../../../api/model/rest";
import {getUser} from "../../../store/view/user";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import _ = require("underscore");
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";

/**
 * Lead assignee changed event.
 */
export default class LeadAssigneeEvent extends LeadEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} leadId lead id.
	 * @param {LeadEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber lead reference number.
	 * @param {LeadAssigneeEventData} data lead assignee change event data.
	 */
	constructor(public readonly id: string,
		public readonly leadId: string,
		public readonly type: LeadEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: LeadAssigneeEventData) {
		super(id, leadId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: LeadEventApiDto): LeadAssigneeEvent {
		const data = LeadAssigneeEventData.fromJson(json.data as LeadAssigneeEventDataApiDto);
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new LeadAssigneeEvent(json.id, json.leadId, LeadEventTypeEnum.AssigneesChanged, entity, moment(json.createdAt),
			json.referenceNumber, data);
	}

	getIcon() {
		return this.isAssigneeRemoved() ? Icon.X : Icon.User;
	}

	getActivityMessage(): string {
		return this.getAssigneesMessage();
	}

	/**
	 * Display previous assignees full names
	 * @return {string}
	 */
	displayPreviousAssignees() {
		const previousAssignees = this.data.previousAssignees.map(
			id => getUser(id).map(u => u.getDisplayName()).getOrElse("")).join(", ");
		return previousAssignees ? previousAssignees : "No assignees";
	}

	/**
	 * Get changed assignees message.
	 * @return {string}
	 */
	getAssigneesMessage() {
		const added = _.difference(this.data.currentAssignees, this.data.previousAssignees);
		const removed = _.difference(this.data.previousAssignees, this.data.currentAssignees);
		let toReturn = "";

		if (added.length) {
			toReturn = (this.getJoinedUserDisplayNames(added) + " added to assignees")
		}
		if (removed.length) {
			if (added.length) {
				toReturn += " / "
			}
			toReturn += (this.getJoinedUserDisplayNames(removed) + " removed from assignees")
		}
		return toReturn;
	}

	/**
	 * Indicates if assignee has been removed.
	 * @return {boolean}
	 */
	isAssigneeRemoved() {
		return (this.data.currentAssignees.length - this.data.previousAssignees.length) < 0;
	}

	/**
	 * Display current assignees full names
	 * @return {string}
	 */
	displayCurrentAssignees() {
		const currentAssignees = this.getJoinedUserDisplayNames(this.data.currentAssignees);
		return currentAssignees ? currentAssignees : "No assignees";
	}

	getJoinedUserDisplayNames(userIds: string[]): string {
		return userIds.map(id => getUser(id).map(u => u.getDisplayName()).getOrElse("")).join(", ");
	}
}

export class LeadAssigneeEventData {
	constructor(public readonly previousAssignees: string[],
		public readonly currentAssignees: string[]) {}

	public static fromJson(json: LeadAssigneeEventDataApiDto): LeadAssigneeEventData {
		return new LeadAssigneeEventData(json.previousAssignees, json.currentAssignees);
	}
}
